import { createContext, useCallback, useContext, useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react'
import { buildVariantPrompt, buildOpenVariantPrompt, substitutePhotoSlots, usedPhotoSlotNums } from '../utils/variantPrompt'
import { getAttachedStyleCard, setAttachedActiveVariant, setAttachedUserValue, subscribeAttachedStyleCard } from '../api/attachedStyleCard'

// ── Демо-переменные карточки — ЕДИНЫЙ источник истины ────────────────
// Живёт НАД обоими GenerateProvider'ами (App-level и внутренний у /bots-виджета,
// см. App.tsx / GenerateWidget.tsx), поэтому демо-блок «Популярных» и чипы в
// виджете читают/пишут ОДНО состояние — без рассинхрона. Одна выбранная
// вариация, один канал вставки (promptAnim → анимация в GenerateInputBar).

/** Чип демо-переменной карточки (label + кусок промпта). */
export interface VariantChip { key: string; label: string; patch: string
	/** «Свой стиль»: open-вариация — юзер вписывает значение вместо {value}. */
	isOpen?: boolean
	placeholder?: string
	/** Превью вариации (after_url) — мини-картинка в выпадашке стиля. */
	afterUrl?: string
	/** Тип значения open-вариации: number = степпер с жёстким клампом
	 *  (владелец 2026-08-14, например «возраст»). Отсутствует = текст. */
	valueType?: 'text' | 'number'
	minValue?: number | null
	maxValue?: number | null
	/** Своё «поле юзера» ЭТОГО стиля (владелец 2026-08-16): перекрывает общий
	 *  cardUserInput — у стиля A число «Возраст», у стиля B текст «Имя». */
	userInput?: CardUserInputCfg | null }

/** Карточное «поле юзера» (Мультистиль, владелец 2026-08-14): живёт РЯДОМ с
 *  выбором стиля, а не как конкурирующая open-вариация. Введённое значение
 *  встаёт на место {value} в патче ВЫБРАННОГО стиля (или в базе). */
export interface CardUserInputCfg {
	label?: string
	placeholder?: string
	type?: 'text' | 'number'
	min?: number | null
	max?: number | null
}

interface VariantContextValue {
	variantChips: VariantChip[]
	activeVariantKey: string
	/** Конфиг карточного поля (null — у карточки его нет). */
	cardUserInput: CardUserInputCfg | null
	/** Конфиг поля АКТИВНОГО стиля: его собственный userInput, иначе общий
	 *  cardUserInput (правило перекрытия, владелец 2026-08-16). */
	activeUserInput: CardUserInputCfg | null
	/** Значение поля активного источника: у стиля со СВОИМ полем — своё
	 *  (ключ = key стиля), у общего поля — одно на все стили ('__card__'). */
	cardUserValue: string
	/** Активный стиль реально подставляет {value} (плейсхолдер в его патче или
	 *  в базе). Поле значения показывается ТОЛЬКО у таких стилей (владелец
	 *  2026-08-16: не каждый стиль мультистиля использует число). */
	activeUsesValue: boolean
	/** Номера фото-слотов, которые использует АКТИВНЫЙ стиль ({photoN} в его
	 *  патче или в базе); null — вариаций нет (фильтра нет, слоты как есть).
	 *  Слоты в виджете показываются и требуются только по этому набору. */
	activePhotoSlotNums: number[] | null
	/** Ввод в карточное поле: промпт активной вариации пересобирается сразу
	 *  (в классическом баре печатается только изменившийся кусок). */
	setUserValue: (v: string) => void
	/** id применённой карточки-переменной (совпадает с card.id, `popular-<id>`). */
	variantCardId: string | null
	/** Сигнал анимированной вставки для GenerateInputBar (nonce меняется). */
	promptAnim: { target: string; nonce: number } | null
	/** Загрузить карточку (чипы+база) при применении. Промпт ставит applyCard. */
	setVariantCard: (
		chips: VariantChip[],
		base: string,
		cardId: string,
		initialKey?: string,
		userInput?: CardUserInputCfg | null,
		initialUserValues?: string | Record<string, string>,
	) => void
	clearVariantCard: () => void
	/** Выбрать вариацию → анимированная вставка (клик по чипу в любом месте). */
	selectVariant: (key: string) => void
	/** «Свой стиль»: выбрать open-вариацию с живым значением инпута —
	 *  {value} в шаблоне патча заменяется на введённое. */
	selectCustomVariant: (key: string, value: string) => void
	/** Актуальный nonce печати. Смена карточки → -1 (инвалидация). Эффект и его
	 *  typeNext сверяют СВОЙ nonce с этим ref'ом и выходят, если устарел — так
	 *  старая печать не перезатирает свежий промпт применённой карточки. */
	latestNonce: MutableRefObject<number>
}

const VariantContext = createContext<VariantContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useVariant(): VariantContextValue {
	const ctx = useContext(VariantContext)
	if (!ctx) throw new Error('useVariant must be used inside <VariantProvider>')
	return ctx
}

export function VariantProvider({ children }: { children: ReactNode }) {
	const [variantChips, setVariantChips] = useState<VariantChip[]>([])
	const [variantBase, setVariantBase] = useState('')
	const [variantCardId, setVariantCardId] = useState<string | null>(null)
	const [activeVariantKey, setActiveVariantKey] = useState('')
	const [cardUserInput, setCardUserInput] = useState<CardUserInputCfg | null>(null)
	// Значения полей по ИСТОЧНИКУ конфига: общий cardUserInput → '__card__'
	// (одно значение на все стили), своё поле стиля → его key (независимые).
	const [userValues, setUserValues] = useState<Record<string, string>>({})
	const [promptAnim, setPromptAnim] = useState<{ target: string; nonce: number } | null>(null)
	const promptAnimNonce = useRef(0)
	const latestNonce = useRef(0)
	// Зеркало variantCardId для подписки на пин (замыкание колбэка иначе видит устаревший стейт).
	const variantCardIdRef = useRef<string | null>(null)

	const setVariantCard = useCallback(
		(chips: VariantChip[], base: string, cardId: string, initialKey?: string, userInput?: CardUserInputCfg | null, initialUserValues?: string | Record<string, string>) => {
			latestNonce.current = -1 // инвалидировать печать: новая карточка перекрывает
			setVariantChips(chips)
			setVariantBase(base)
			setVariantCardId(cardId)
			variantCardIdRef.current = cardId
			setActiveVariantKey(initialKey && chips.some((c) => c.key === initialKey) ? initialKey : chips[0]?.key ?? '')
			setCardUserInput(userInput ?? null)
			// Восстановление (GTM после F5/перехода): значения из пина, иначе чисто.
			// Легаси-строка = значение ОБЩЕГО поля.
			setUserValues(
				typeof initialUserValues === 'string'
					? initialUserValues
						? { __card__: initialUserValues }
						: {}
					: initialUserValues ?? {},
			)
		},
		[],
	)

	const clearVariantCard = useCallback(() => {
		latestNonce.current = -1
		setVariantChips([])
		setVariantBase('')
		setVariantCardId(null)
		variantCardIdRef.current = null
		setActiveVariantKey('')
		setCardUserInput(null)
		setUserValues({})
	}, [])

	// Карточку открепили ЛЮБЫМ путём (кнопка «Очистить»/reset виджета, крестик
	// пина, авто-смена модели, уход со страницы) → detachStyleCard шлёт CHANGED_
	// EVENT, и в колбэке подписки мы чистим осиротевшие демо-чипы. Один триггер
	// на все точки открепления — никакой «разной логики на одном функционале».
	// Паттерн «подписка на внешний источник + setState в колбэке» (а не setState
	// в теле эффекта) — рекомендованный react-hooks; на attach карточка не null,
	// поэтому применение новой карточки чипы не сбрасывает.
	useEffect(() => {
		return subscribeAttachedStyleCard(() => {
			const attached = getAttachedStyleCard()
			if (!attached) {
				clearVariantCard()
				return
			}
			// Прикрепили ДРУГУЮ карточку (22.10): чипы прошлой не должны пережить смену — иначе окно инструмента
			// показывало стили и поле прежней карточки и требовало его заполнить (залипание, аудит мультистилей).
			// Свою карточку (тот же id, с префиксом popular- у «Популярных» и без него у стилей) не трогаем: её чипы
			// ставит применение (applyVariantPrompt) или восстановление из пина.
			const held = variantCardIdRef.current
			if (attached.id && held && held !== attached.id && held !== `popular-${attached.id}`) clearVariantCard()
		})
	}, [clearVariantCard])

	// Источник поля для стиля: своё поле стиля перекрывает общее; ключ значения
	// у своего поля — key стиля (независимые), у общего — '__card__' (одно на всех).
	const fieldCfgFor = useCallback(
		(key: string): { cfg: CardUserInputCfg | null; valueKey: string } => {
			const chip = variantChips.find((c) => c.key === key)
			if (chip?.userInput) return { cfg: chip.userInput, valueKey: key }
			return { cfg: cardUserInput, valueKey: '__card__' }
		},
		[variantChips, cardUserInput],
	)

	const activeField = fieldCfgFor(activeVariantKey)
	const activeUserInput = activeField.cfg
	const cardUserValue = userValues[activeField.valueKey] ?? ''

	const selectVariant = useCallback(
		(key: string) => {
			setActiveVariantKey(key)
			setAttachedActiveVariant(key) // пин помнит выбранный стиль — после F5 возвращаемся к нему, а не к стартовому
			const patch = variantChips.find((c) => c.key === key)?.patch ?? ''
			promptAnimNonce.current += 1
			latestNonce.current = promptAnimNonce.current // этот запуск — актуальный
			// {value}: значение поля НОВОГО стиля (своё или общее); стиль без
			// поля — плейсхолдер чистится.
			const f = fieldCfgFor(key)
			const sub = f.cfg ? (userValues[f.valueKey] ?? '').trim() : ''
			const target = substitutePhotoSlots(buildVariantPrompt(variantBase, patch).replace(/\{value\}/g, sub)).replace(/\n{3,}/g, '\n\n')
			setPromptAnim({ target, nonce: promptAnimNonce.current })
		},
		[variantChips, variantBase, fieldCfgFor, userValues],
	)

	// Ввод карточного поля: пишем значение АКТИВНОГО источника + пересборка
	// промпта активной вариации.
	const setUserValue = useCallback(
		(v: string) => {
			const f = fieldCfgFor(activeVariantKey)
			setUserValues((prev) => ({ ...prev, [f.valueKey]: v }))
			// Зеркало в пин (тихо): значения переживают F5 — restored вернёт map.
			setAttachedUserValue(f.valueKey, v)
			if (!f.cfg) return
			const patch = variantChips.find((c) => c.key === activeVariantKey)?.patch ?? ''
			promptAnimNonce.current += 1
			latestNonce.current = promptAnimNonce.current
			const target = substitutePhotoSlots(buildVariantPrompt(variantBase, patch).replace(/\{value\}/g, v.trim())).replace(/\n{3,}/g, '\n\n')
			setPromptAnim({ target, nonce: promptAnimNonce.current })
		},
		[variantChips, variantBase, activeVariantKey, fieldCfgFor],
	)

	// «Свой стиль»: выбор open-вариации с ЖИВЫМ значением инпута. Семантика —
	// buildOpenVariantPrompt: шаблон с {value} → обёртка ввода; ПУСТОЙ шаблон →
	// ввод как есть на место {variant}; {value} в базе тоже заменяется.
	// variantBase приватен — потому подстановка живёт здесь, в едином
	// источнике правды (подсветка, промпт, F5 и классический бар без спец-кейсов).
	// Показ поля значения зависит от ВЫБРАННОГО стиля: {value} в его патче или
	// в базе. Стили без плейсхолдера поле не показывают (и не подставляют).
	const activeUsesValue =
		!!activeUserInput &&
		(/\{value\}/.test(variantChips.find((c) => c.key === activeVariantKey)?.patch ?? '') ||
			/\{value\}/.test(variantBase))

	// Фото-слоты активного стиля: union номеров из его патча и базы. Вариаций
	// нет — null (обычная карточка: слоты живут как объявлены).
	const activePhotoSlotNums = variantChips.length
		? usedPhotoSlotNums(
				(variantChips.find((c) => c.key === activeVariantKey)?.patch ?? '') + ' ' + variantBase,
			)
		: null

	const selectCustomVariant = useCallback(
		(key: string, value: string) => {
			setActiveVariantKey(key)
			setAttachedActiveVariant(key)
			const template = variantChips.find((c) => c.key === key)?.patch ?? ''
			promptAnimNonce.current += 1
			latestNonce.current = promptAnimNonce.current
			setPromptAnim({ target: substitutePhotoSlots(buildOpenVariantPrompt(variantBase, template, value)), nonce: promptAnimNonce.current })
		},
		[variantChips, variantBase],
	)

	const value: VariantContextValue = {
		variantChips,
		activeVariantKey,
		cardUserInput,
		activeUserInput,
		cardUserValue,
		activeUsesValue,
		activePhotoSlotNums,
		setUserValue,
		variantCardId,
		promptAnim,
		setVariantCard,
		clearVariantCard,
		selectVariant,
		selectCustomVariant,
		latestNonce,
	}

	return <VariantContext.Provider value={value}>{children}</VariantContext.Provider>
}
